import { useState } from "react";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useRefresh,
  useUpdate,
} from "ra-core";
import { format, isBefore, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "react-router";
import { Check, ListChecks } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { NextActionBadge, PopulationBadge } from "../contacts/ContactBadges";
import { getContactDisplayName } from "../contacts/contactModel";
import { Chip } from "../shared/Chip";
import { EmptyState } from "../shared/EmptyState";
import type { Contact, NextActionType } from "../types";

// Sprint 4 T4.4.b + Sprint 9.2 + Sprint 10.x (spec Théo "Action du jour") :
// - Liste des contacts dont next_action_date <= today (filtre exclut futurs)
// - Checkbox quick-done : crée note auto + PATCH next_action_type=aucune
// - Chip "En retard" si next_action_date < today (strict)
// - PopulationBadge inline (tag Plateforme/Prospection visible)

const ACTION_LABEL_PAST: Record<NextActionType, string> = {
  a_relancer: "Relance",
  a_appeler: "Appel",
  a_mailer: "Démo (mail)",
  a_engager: "Engagement",
  aucune: "Action",
};

export const MyDayActionsList = () => {
  const { identity } = useGetIdentity();
  const todayIso = format(new Date(), "yyyy-MM-dd");

  const firstName = identity?.fullName?.split(" ")[0] ?? "";
  const { data, isPending } = useGetList<Contact>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "next_action_date", order: "ASC" },
      filter: {
        // CRM partagé : pas de filtre sales_id, tout le monde voit toutes
        // les actions de l'équipe (cf feedback_no_sales_id_filter memory).
        "next_action_date@lte": todayIso,
        "next_action_type@neq": "aucune",
        "archived_at@is": null,
      },
    },
    { enabled: !!identity?.id },
  );

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListChecks className="size-4 text-fabrik-accent-text" />
            <p className="text-[13px] font-semibold text-foreground">
              Actions du jour
            </p>
          </div>
          {data?.length ? (
            <Badge variant="secondary" className="text-[11px]">
              {data.length}
            </Badge>
          ) : null}
        </div>

        {isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : !data?.length ? (
          <EmptyState
            variant="celebrate"
            icon={<Check className="size-6" />}
            title="Toutes les actions sont traitées"
            description={
              firstName ? `Bonne journée ${firstName} ✌` : "Bonne journée ✌"
            }
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border/40">
            {data.map((contact) => (
              <ActionRow key={contact.id} contact={contact} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

function ActionRow({ contact }: { contact: Contact }) {
  const { identity } = useGetIdentity();
  const [createNote] = useCreate();
  const [update] = useUpdate();
  const refresh = useRefresh();
  const [isMarking, setIsMarking] = useState(false);

  const isOverdue =
    !!contact.next_action_date &&
    isBefore(new Date(contact.next_action_date), startOfDay(new Date()));

  const buildNoteText = (): string => {
    const type = (contact.next_action_type ?? "aucune") as NextActionType;
    const typeLabel = ACTION_LABEL_PAST[type] ?? "Action";
    const dateLabel = contact.next_action_date
      ? format(new Date(contact.next_action_date), "d MMMM yyyy", {
          locale: fr,
        })
      : format(new Date(), "d MMMM yyyy", { locale: fr });
    return `✓ Action faite : ${typeLabel} · ${dateLabel}`;
  };

  const handleDone = async (checked: boolean | "indeterminate") => {
    if (checked !== true || isMarking) return;
    setIsMarking(true);

    // 1. Note auto archivée dans timeline.
    let noteCreated = false;
    try {
      await new Promise<void>((resolve, reject) =>
        createNote(
          "contact_notes",
          {
            data: {
              contact_id: contact.id,
              text: buildNoteText(),
              sales_id: identity?.id,
              date: new Date().toISOString(),
            },
          },
          {
            onSuccess: () => {
              noteCreated = true;
              resolve();
            },
            onError: (err) => reject(err),
          },
        ),
      );
    } catch {
      toast.error("Impossible d'archiver la note", {
        description: "La timeline n'a pas été mise à jour. Réessaie.",
      });
      setIsMarking(false);
      return;
    }

    // 2. PATCH contact → next_action_type = 'aucune' + date = null.
    try {
      await new Promise<void>((resolve, reject) =>
        update(
          "contacts",
          {
            id: contact.id,
            data: { next_action_type: "aucune", next_action_date: null },
            previousData: contact,
          },
          {
            mutationMode: "optimistic",
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        ),
      );
      toast.success("Action marquée faite");
      refresh();
    } catch {
      if (noteCreated) {
        toast.error("Note ajoutée mais action non clôturée", {
          description:
            "La timeline a bien la trace, mais next_action n'a pas été remise à zéro.",
        });
      } else {
        toast.error("Échec de la mise à jour");
      }
      setIsMarking(false);
    }
  };

  return (
    <li className="py-2 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <Checkbox
          checked={false}
          onCheckedChange={handleDone}
          disabled={isMarking}
          aria-label={`Marquer fait pour ${contact.first_name} ${contact.last_name ?? ""}`}
          className={cn("shrink-0", isMarking && "opacity-50")}
        />
        <Link
          to={`/contacts/${contact.id}/show`}
          className={cn(
            "flex items-center justify-between gap-3 min-w-0 flex-1 hover:opacity-80",
            isMarking && "opacity-50 pointer-events-none",
          )}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-medium truncate">
                {getContactDisplayName(contact)}
              </p>
              <PopulationBadge value={contact.population} />
              {isOverdue && <Chip variant="red">En retard</Chip>}
            </div>
            {contact.company_name && (
              <p className="text-xs text-muted-foreground truncate">
                {contact.company_name}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <NextActionBadge
              type={contact.next_action_type}
              date={contact.next_action_date}
            />
          </div>
        </Link>
      </div>
    </li>
  );
}
